import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { Hierarchy, WorkspaceHierarchy } from '@ardoq/data-model';

/**
 * Indexes components by their parent and workspace. eg.
 * {
 *  workspace1: {
 *   null: ['component1', 'component2'],
 *  'component1': ['component3']
 * }
 * }
 */
export const generateHierarchy = (
  components: Pick<APIComponentAttributes, '_id' | 'parent' | 'rootWorkspace'>[]
): Hierarchy => {
  const hierarchy = new Map();
  components.forEach(component => {
    const {
      _id: componentId,
      parent: parentId = null,
      rootWorkspace,
    } = component;

    const wsHierarchy = hierarchy.get(rootWorkspace) || new Map();
    if (!hierarchy.has(rootWorkspace)) {
      hierarchy.set(rootWorkspace, wsHierarchy);
    }
    const componentIds = wsHierarchy.get(parentId) || [];
    if (!wsHierarchy.has(parentId)) {
      wsHierarchy.set(parentId, componentIds);
    }
    componentIds.push(componentId);
  });
  return hierarchy;
};

/**
 * Recursively finds all descendant ids of a component, excluding itself
 */
export const getDescendantIdsFromHierarchy = (
  workspaceHierarchy: WorkspaceHierarchy,
  componentId: ArdoqId
): ArdoqId[] => {
  const childrenIds = workspaceHierarchy.get(componentId) || [];
  return childrenIds.flatMap(id => [
    id,
    ...getDescendantIdsFromHierarchy(workspaceHierarchy, id),
  ]);
};

/**
 *  Recursively traverse the hierarchy to find the level of the component.
 *  Low level means closer to the root.
 */
export const getLevel = (
  workspaceHierarchy: WorkspaceHierarchy,
  componentId: ArdoqId,
  level = 1
): number => {
  const [parentId] = Array.from(workspaceHierarchy.entries()).find(
    ([_, children]) => children.includes(componentId)
  ) ?? [null];

  if (!parentId) {
    return level;
  }
  return getLevel(workspaceHierarchy, parentId, level + 1);
};
